import {
	BackdropCircularProgress,
	EnhancedSnackbar,
	IResponse,
	IServerError,
	ITest,
	LabelPosition,
	RHookFormTextFieldWithLabel,
	SingleColumnLayout,
	StandardPanel,
	StandardSubmitFormButtonsToolbar,
} from "@websential/cosmic-v20";
import {
	DATA_ROLE_SCREEN_SLUG,
	INITIAL_SNACKBAR_DATA,
} from "../../../common/constants";
import { FC, useEffect, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import {
	setSelectedActivityLogActions,
	setSelectedStickyNavBar,
} from "../../../store/actions";
import { useDispatch, useSelector } from "react-redux";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate, useParams } from "react-router-dom";

import { DataRoleSchema } from "./validator";
import { DataRoleService } from "../../../services/DataRoleService";
import { Grid } from "@mui/material";
import { IAppState } from "../../../store/reducers";
import { IDataRoleRequestPayload } from "../../../models/dataRole";
import { instanceOfIDataRoleResponce } from "../../../common/instance-method";
import { setSelectedDrawerOptionsMenu } from "../../../store/actions/drawerMenuOptionsActions";
import { yupResolver } from "@hookform/resolvers/yup";

export interface IDataRoleSetup extends ITest { }

const DEFAULT_VALUES = {
	roleName: "",
	appRoleName: "",
};

const SINGLE_RECORD_DATA_QUERY_KEY: any = "get-data-roles-list";

const useGetData = (id: string | undefined) =>
	useQuery({
		// [SINGLE_RECORD_DATA_QUERY_KEY, id],
		queryKey: [SINGLE_RECORD_DATA_QUERY_KEY],
		queryFn: (data) => {
			return DataRoleService.getById(id);
		},
		refetchOnWindowFocus: false,
	});

export const DataRoleSetup: FC<IDataRoleSetup> = ({ testId }) => {
	const navigate = useNavigate();
	const dispatch = useDispatch();
	const [snackbarData, setSnackbarData] = useState(INITIAL_SNACKBAR_DATA);

	// const [metaFieldIds, setMetaFieldIds] = useState({});
	// const [renderForm, setRenderForm] = useState(false);
	const queryClient = useQueryClient();
	const { id } = useParams();

	// const [editRecordId, setEditRecordId] = useState(id);

	const { isPending: isSubmitting, mutate: submitFormData } = useMutation({
		mutationFn: DataRoleService.create,
		onSuccess: (res: IDataRoleRequestPayload | IServerError | IResponse) => {
			if (instanceOfIDataRoleResponce(res)) {
				queryClient.invalidateQueries({
					queryKey: [SINGLE_RECORD_DATA_QUERY_KEY, res.id],
				});
				setSnackbarData({
					open: true,
					message: "Success",
					severity: "success",
				});
			}
			let path = "/data-roles";
			navigate(path);
		},
		onError: (err) => {
			// TODO: customize the toast message based on the response
			setSnackbarData({
				open: true,
				message: "An error occurred, please try again",
				severity: "error",
			});
		},
	});

	const { isPending: isPendingData, data: selectedDataRecord } = useGetData(id);


	const formMethods = useForm<any>({
		resolver: yupResolver(DataRoleSchema),
		mode: "onBlur",
		reValidateMode: "onChange",
		defaultValues: DEFAULT_VALUES,
	});
	const onSubmit = (formData: IDataRoleRequestPayload) => {
		submitFormData(formData);

	};

	const onResetClicked = () => {
		setSnackbarData({
			open: true,
			message: "Success",
			severity: "success",
		});
		formMethods.reset(DEFAULT_VALUES);
	};

	const onSnackbarClosed = (
		_event: React.SyntheticEvent | Event,
		reason?: string
	) => {
		if (reason === "clickaway") {
			return;
		}
		setSnackbarData(INITIAL_SNACKBAR_DATA);
	};

	useEffect(() => {
		// if the selected flex field data has loaded.
		if (selectedDataRecord) {
			Object.keys(selectedDataRecord).forEach((fieldName) => {

				// @ts-ignore
				const fieldValue = selectedDataRecord[fieldName];

				// @ts-ignore
				formMethods.setValue(fieldName, fieldValue ?? undefined);
			});
		}
	}, [selectedDataRecord, formMethods.setValue, formMethods]);

	const drawerMenuOptions = useSelector(
		(state: IAppState) => state.drawerMenu.menu
	);

	const expandedLabels = ["Misc.", "Data Roles"];
	const expandedBanckSetupItem = "Setup";
	const enhanceData = (data: any[], parentLabel?: string): any[] => {
		return data.map((item) => {
			let isExpanded = expandedLabels.includes(item.label);
			if (
				parentLabel === "Data Roles" &&
				item.label === expandedBanckSetupItem
			) {
				isExpanded = true;
			}
			const enhancedItem = {
				...item,
				expanded: isExpanded,
				items: item.items ? enhanceData(item.items, item.label) : undefined,
			};
			return enhancedItem;
		});
	};

	useEffect(() => {
		dispatch(
			setSelectedActivityLogActions({
				entityId1: "",
				entityId2: "",
				entityId3: "",
				screenName: DATA_ROLE_SCREEN_SLUG,
				companyName: "",
				activityType: "activity_type=create&activity_type=updated",
				activityLogVisibility: true,
			})
		);
		dispatch(setSelectedStickyNavBar({ isSticky: true }));
		if (drawerMenuOptions && drawerMenuOptions.length > 0) {
			const enhancedData = enhanceData(drawerMenuOptions);
			dispatch(setSelectedDrawerOptionsMenu({ menu: enhancedData }));
		}
	}, [drawerMenuOptions]);

	return (
		<Grid
			size={{ xs: 12, sm: 12, md: 12, lg: 12, xl: 12 }}
		// sx={{ "& .MuiAppBar-root": { zIndex: 2, top: "65px" } }}
		>
			<BackdropCircularProgress
				open={isSubmitting || isPendingData}
				testId={`${testId}-backdrop`}
			/>
			<Grid>
				<FormProvider {...formMethods}>
					<form onSubmit={formMethods.handleSubmit(onSubmit)}>
						<SingleColumnLayout
							children={
								<StandardPanel
									title="Data Role"
									testId={`${testId}-data-role`}
									leftSpacing={2}
									rightSpacing={2}
								>
									<RHookFormTextFieldWithLabel
										id="roleName"
										testId={`${testId}-role`}
										label="Data Role"
										labelPosition={LabelPosition.Left}
										required
									/>
								</StandardPanel>
							}
							toolbar={
								<StandardSubmitFormButtonsToolbar
									onResetClicked={onResetClicked}
									isSubmitting={false}
									testId="standard-form-submit-toolbar"
								/>
							}
							testId="form-layout"
						/>
					</form>
				</FormProvider>
				<EnhancedSnackbar
					message={snackbarData.message}
					onCloseHandler={onSnackbarClosed}
					severity={snackbarData.severity}
					testId={`${testId}-snackbar`}
					open={snackbarData.open}
				/>
			</Grid>
		</Grid>
	);
};
